import React, { memo, ReactElement, useCallback, useContext, useMemo } from 'react'

import { Table } from 'antd'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import { TemporaryDataType, TemporarySetDataType } from './useTemporaryDataState'
import { cementColumns } from './static-data-columns'
import { EditableCell, EditableRow } from './editable-table-utils'
import { emptyCementPlug } from '../utils/emptyEntities'
import { CustomTableHeader } from './components/CustomTableHeader'
import { saveAllTemporaryData } from '../importer-api'
import { deleteEntityAndItsChildren, deletedCascadeEntities } from '../utils/actions'
import { StaticDataImporterContext } from '../context/StaticDataImporterContext'
import { getSelectedRowKeys, useSelectAllRows } from '../hooks/useSelectAllRows'
import { useNavigateToNewRow } from '../hooks/useNavigateToNewRow'
import { RowSelectionType } from 'antd/lib/table/interface'
import { useMoveEntityRowToAnotherEntityRowParent } from '../hooks/useMoveEntityRowToAnotherEntityRowParent'

type EditableTableProps = Parameters<typeof Table>[0]

type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>

const TemporaryCementPlugTable = memo(
    ({
        parent,
        data,
        setData,
        localErrors,
        removeErrorWhenCheckedRowIsRemoved
    }: {
        parent: RunDto
        data: TemporaryDataType
        setData: TemporarySetDataType
        localErrors: LocalErrorsTypes[]
        removeErrorWhenCheckedRowIsRemoved: (value: TemporaryDataType) => void
    }): ReactElement => {
        const {
            checkedRows,
            setCheckedRows,
            formatCheckedRows,
            setIsValidToTransfer,
            updateValueFromCheckedEntity
        } = useContext(StaticDataImporterContext)

        const { selectAllRows } = useSelectAllRows()

        const { goToNewRow, classNameNewRow } = useNavigateToNewRow()
        const { moveRowToAnotherParent } = useMoveEntityRowToAnotherEntityRowParent()

        const cements = useMemo(
            () =>
                data.cements
                    .filter(cem => cem.parent == parent.nodeId)
                    .map(cement => ({
                        ...cement,
                        key: String(cement.rowId),
                        parent: parent.nodeId
                    })),
            [data.cements, parent.nodeId]
        )

        // Functions
        const handleEditData = useCallback(
            (entity: EntityDto[], row: EntityDto, setter: TemporarySetDataType): void => {
                const newData = [...entity]
                const index = newData.findIndex(item => row.rowId === item.rowId)
                const item = newData[index]
                if (item) {
                    saveAllTemporaryData(row, 'CEMENT_PLUG').then(saves => {
                        row.rowId = saves[0].id
                        row.key = saves[0].id
                        newData.splice(index, 1, {
                            ...item,
                            ...row
                        })
                        setter.setCements(newData as CementedPlugDto[])
                        setIsValidToTransfer(false)
                        updateValueFromCheckedEntity(`cements=>${row.parent}`, row)
                    })
                }
            },
            [setIsValidToTransfer, updateValueFromCheckedEntity]
        )

        const handleEditSave = useCallback(
            (row: EntityDto): void => {
                handleEditData(data.cements, row, setData)
            },
            [data.cements, handleEditData, setData]
        )

        const handleAddNewRow = useCallback((): void => {
            const oldEntity = [...data.cements]

            const newEntity: CementedPlugDto = {
                ...emptyCementPlug,
                key: undefined,
                frontParentUuid: parent.frontUuid,
                parent: parent.nodeId,
                nodeId: Date.now(),
                rowId: undefined,
                frontUuid: uuidv4()
            }

            saveAllTemporaryData(newEntity, 'CEMENT_PLUG').then(saves => {
                newEntity.rowId = saves[0].id
                newEntity.key = saves[0].id
                newEntity.newRow = true
                oldEntity.push(newEntity)
                setData.setCements(oldEntity)
                goToNewRow()
            })
        }, [data.cements, goToNewRow, parent.frontUuid, parent.nodeId, setData])

        const handleDelete = useCallback(
            (record: EntityDto): void => {
                const newData = data.cements.filter(item => item.rowId !== record.rowId)
                const newCheckedRows = deletedCascadeEntities(checkedRows, record)

                deleteEntityAndItsChildren(record.rowId).then(() => {
                    const newCheckedToTransfer = formatCheckedRows(newCheckedRows)

                    setData.setCements(newData)
                    setCheckedRows(newCheckedRows)
                    removeErrorWhenCheckedRowIsRemoved(newCheckedToTransfer)
                })
            },
            [checkedRows, data.cements, formatCheckedRows, removeErrorWhenCheckedRowIsRemoved, setCheckedRows, setData]
        )

        const components = useMemo(
            () => ({
                body: {
                    row: EditableRow,
                    cell: EditableCell
                }
            }),
            []
        )

        const cementCols = useMemo(
            () =>
                cementColumns(
                    handleDelete,
                    handleEditSave,
                    (record: EntityDto, selectedParent: number) =>
                        moveRowToAnotherParent(record, selectedParent, handleEditSave),
                    data.runs,
                    localErrors
                ),
            [data.runs, handleDelete, handleEditSave, localErrors, moveRowToAnotherParent]
        )

        const rowSelection = useMemo(
            () => ({
                selectedRowKeys: getSelectedRowKeys(checkedRows, 'cements', parent),
                type: 'checkbox' as RowSelectionType,
                onChange: (selectedRowsKey: React.Key[], selectedRows: CementedPlugDto[]) => {
                    selectAllRows({
                        data,
                        parentNodeId: parent.nodeId,
                        selectedRows,
                        type: 'cements'
                    })
                },
                onSelect: (
                    record: EntityDto,
                    selected: boolean,
                    selectedRows: CementedPlugDto[],
                    nativeEvent: Event
                ) => {
                    selectAllRows({
                        data,
                        parentNodeId: parent.nodeId,
                        type: 'cements',
                        selectedRows,
                        target: record,
                        selected,
                        nativeEvent
                    })
                }
            }),
            [checkedRows, data, parent, selectAllRows]
        )

        const expandable = useMemo(
            () => ({
                defaultExpandAllRows: false
            }),
            []
        )

        return (
            <TableContainer>
                <Table
                    title={() => {
                        return <CustomTableHeader title="cement plugs" handleAddNewRow={handleAddNewRow} />
                    }}
                    rowKey="key"
                    size="small"
                    components={components}
                    rowClassName={(d: EntityDto) => (d?.newRow ? `editable-row ${classNameNewRow}` : 'editable-row')}
                    bordered
                    dataSource={cements}
                    columns={cementCols as ColumnTypes}
                    pagination={false}
                    rowSelection={rowSelection}
                    expandable={expandable}
                />
            </TableContainer>
        )
    }
)

TemporaryCementPlugTable.displayName = 'TemporaryCementPlugTable'

export default TemporaryCementPlugTable

const TableContainer = styled.main``
